import { BznavAppIds, IS_CLIENT } from '@repo/common-utils';

import { ReactNativeBridgeMessage, ReactNativeBridgeResponse, ReactNativeMessageHandler } from '../types';

export class BznavReactNativeBridge {
  private _appId?: BznavAppIds;
  private _isReactNativeWebview = IS_CLIENT && !!window.ReactNativeWebView;
  private _handlers = new Map<string, ReactNativeMessageHandler[]>();
  private _pendingContexts = new Map<
    string,
    {
      resolve: (value: unknown) => void;
      reject: (reason?: ReactNativeBridgeResponse['error']) => void;
      callback: ReactNativeMessageHandler;
    }
  >();

  constructor(appId: BznavAppIds) {
    if (!this._isReactNativeWebview) return;
    this._appId = appId;
    this.setupMessageListener();
  }

  public addMessageHandler(actionName: string, handler: ReactNativeMessageHandler) {
    if (!this._isReactNativeWebview) return;

    const existingHandlers = this._handlers.get(actionName) || [];
    existingHandlers.push(handler);
    this._handlers.set(actionName, existingHandlers);
  }

  public removeMessageHandler(actionName: string, handler?: ReactNativeMessageHandler) {
    if (!this._isReactNativeWebview) return;

    if (!handler) {
      this._handlers.delete(actionName);
      return;
    }

    const existingHandlers = this._handlers.get(actionName);
    if (existingHandlers) {
      const filteredHandlers = existingHandlers.filter((h) => h !== handler);
      if (filteredHandlers.length === 0) {
        this._handlers.delete(actionName);
      } else {
        this._handlers.set(actionName, filteredHandlers);
      }
    }
  }

  private setupMessageListener() {
    window.addEventListener('message', (event: MessageEvent) => {
      try {
        const message: ReactNativeBridgeMessage | ReactNativeBridgeResponse = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (!message.sender || message.sender !== 'bznav-app') return;

        if ('actionName' in message) {
          this.handleMessage(message);
        } else if ('isSuccess' in message) {
          this.handleResponse(message);
        } else {
          console.error('Invalid message format:', message);
        }
      } catch (error) {
        console.error('Error handling message:', error, event);
      }
    });
  }

  private async handleMessage(message: ReactNativeBridgeMessage) {
    const handlers = this._handlers.get(message.actionName);

    if (handlers && handlers.length > 0) {
      try {
        // 모든 핸들러를 순차적으로 실행
        for (const handler of handlers) {
          const response = await handler(message.data);
          if (response) {
            window.ReactNativeWebView.postMessage(JSON.stringify({ ...response, sender: this._appId, messageId: message.messageId }));
          }
        }
      } catch (error) {
        console.error(error);
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            sender: this._appId,
            messageId: message.messageId,
            isSuccess: false,
            error: {
              code: 'UNKNOWN_ERROR',
              message: '알 수 없는 에러가 발생하였습니다.'
            }
          })
        );
      }
    } else {
      console.warn('No handler found for action:', message.actionName);
    }
  }

  private async handleResponse(message: ReactNativeBridgeResponse) {
    if (!message.messageId) return;

    const requestContext = this._pendingContexts.get(message.messageId);
    if (requestContext) {
      this._pendingContexts.delete(message.messageId);
      if (!message.isSuccess) {
        requestContext.reject(message.error);
        return;
      }

      requestContext.callback(message.data);
      requestContext.resolve(message.data);
    }
  }

  public sendNativeMessage(actionName: string, data: unknown, callback?: ReactNativeMessageHandler, timeout?: number) {
    if (!this._isReactNativeWebview) return;
    const messageId = this.generateId();
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        messageId,
        actionName,
        data,
        sender: this._appId
      })
    );

    return new Promise((resolve, reject) => {
      if (!callback) return resolve(null);
      setTimeout(() => {
        this._pendingContexts.delete(messageId);
        reject({ code: 'TIMEOUT', message: '요청시간이 만료되었습니다.', actionName });
      }, timeout ?? 10000);

      this._pendingContexts.set(messageId, { resolve, reject, callback });
    });
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }
}
